import usePermissions from "@hooks/usePermissions";
import {
  IconButton,
  Paper,
  SelectChangeEvent,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
  useTheme,
} from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import DeleteIcon from "src/assets/icon/delete";
import MyTablePagination from "src/components/pagination/TablePagination";
import Scrollbar from "src/components/Scrollbar";
import {
  TableHeadCustom,
  TableNoData,
  TableSelectedActions,
} from "src/components/table";
import useTable from "src/hooks/useTable";
import { getTaxesTableHead } from "../constants";
import { useTaxApi } from "../services/useTaxApi";
import { ITax, ITaxesTableProps } from "../types";
import { TaxesModal } from "./TaxesModal";
import { TaxesTableRow } from "./TaxesTableRow";
import ConfirmDialog from "@components/ConfirmDialog";

const SKELETON = ["", "", "", "", "", ""];

export const TaxesTable: React.FC<ITaxesTableProps> = (props) => {
  const {
    fetchData,
    loader,
    taxes,
    totalRecords,
    controller,
    handleSetController,
    translate,
    selectedComplianceStandard,
  } = props;
  const { deleteTax } = useTaxApi();
  const {
    order,
    orderBy,
    selected,
    onSelectRow,
    setSelected,
    onSelectAllRows,
  } = useTable({
    defaultOrderBy: "code",
  });
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const tableHead = getTaxesTableHead(translate);
  const [isDeleteModalVisible, setIsDeleteModalVisible] =
    useState<boolean>(false);
  const [isEditOpen, setIsEditOpen] = useState<boolean>(false);
  const [editedTaxId, setEditedTaxId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const { permissionHandle } = usePermissions();

  const canWriteTax = permissionHandle(["Taxes_Write"]);

  useEffect(() => {
    setSelected([]);
  }, [selectedComplianceStandard, setSelected]);

  const handleToggleDeleteModal = (id: string | null) => {
    if (id) {
      setSelected([id]);
    } else {
      setSelected([]);
    }
    setIsDeleteModalVisible(!isDeleteModalVisible);
  };

  const handleChangeRowsPerPage = (event: SelectChangeEvent<any>) => {
    handleSetController(0, parseInt(event.target.value, 10));
  };

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    handleSetController(newPage, undefined);
  };

  const deleteMultipleTaxes = async () => {
    setIsDeleting(true);
    try {
      const deletePromises = selected.map((id) => deleteTax(id));
      const results = await Promise.allSettled(deletePromises);
      const successCount = results.filter(
        (r) => r.status === "fulfilled" && (r.value as any).status === 200,
      ).length;
      const failureCount = selected.length - successCount;

      if (successCount > 0 && failureCount === 0) {
        enqueueSnackbar(
          translate("successfully_deleted") ||
            `Successfully deleted ${successCount} taxes`,
          { variant: "success" },
        );
      } else if (successCount > 0 && failureCount > 0) {
        enqueueSnackbar(
          `Successfully deleted ${successCount} taxes, but ${failureCount} failed.`,
          { variant: "warning" },
        );
      } else if (failureCount > 0) {
        enqueueSnackbar(
          translate("delete_failed") || `Failed to delete taxes`,
          { variant: "error" },
        );
      }
      await fetchData();
      setSelected([]);
      setIsDeleteModalVisible(false);
    } catch (error) {
      console.error("Error deleting taxes:", error);
      enqueueSnackbar(translate("something_went_wrong"), { variant: "error" });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleOpenEditModal = (taxId: string) => {
    setEditedTaxId(taxId);
    setIsEditOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditedTaxId(null);
    setIsEditOpen(false);
  };

  const handleDeleteSelected = () => {
    setIsDeleteModalVisible(true);
  };

  const handleSuccess = () => {
    fetchData();
    if (isEditOpen) {
      handleCloseEditModal();
    }
  };

  return (
    <Paper sx={{ width: "100%", mb: 2, borderRadius: 2 }}>
      {isDeleteModalVisible && (
        <ConfirmDialog
          open={isDeleteModalVisible}
          onClose={() => setIsDeleteModalVisible(false)}
          title={translate("delete_tax")}
          content={""}
          entityName={"tax"}
          entityCount={selected?.length}
          confirmText="delete" 
          cancelText="cancel_button" 
          confirmLoading={isDeleting}
          onConfirm={deleteMultipleTaxes}
          delteConfirmation={true}
        />
      )}
      {isEditOpen && (
        <TaxesModal
          open={isEditOpen}
          handleCloseModal={handleCloseEditModal}
          taxId={editedTaxId || undefined}
          onSuccess={handleSuccess}
          translate={translate}
          selectedComplianceStandard={selectedComplianceStandard}
        />
      )}
      <Scrollbar>
        <TableContainer sx={{ minWidth: 800, position: "relative" }}>
          {selected.length > 0 && (
            <TableSelectedActions
              numSelected={selected.length}
              rowCount={taxes?.filter((row: ITax) => row?.isActive).length}
              onSelectAllRows={(checked) =>
                onSelectAllRows(
                  checked,
                  taxes
                    ?.filter((row: ITax) => row?.isActive)
                    .map((row: ITax) => row?.id) as string[],
                )
              }
              actions={
                <Stack spacing={1} direction="row">
                  <Tooltip title={translate("delete")}>
                    <IconButton
                      color="primary"
                      onClick={handleDeleteSelected}
                      disabled={!canWriteTax}
                    >
                      <DeleteIcon customColor={theme.palette.action.active} />
                    </IconButton>
                  </Tooltip>
                </Stack>
              }
            />
          )}
          <Table size="small">
            <TableHeadCustom
              order={order}
              orderBy={orderBy}
              headLabel={tableHead}
              rowCount={taxes?.length ?? 0}
              onSelectAllRows={(checked) =>
                onSelectAllRows(
                  checked,
                  taxes
                    ?.filter((row: ITax) => row?.isActive)
                    .map((row: ITax) => row?.id) as string[],
                )
              }
            />
            <TableBody>
              {loader ? (
                SKELETON.map((_, index) => (
                  <TableRow key={index}>
                    <TableCell padding="checkbox">
                      <Skeleton
                        sx={{ marginLeft: 1.5 }}
                        width="100%"
                        animation="wave"
                        variant="text"
                      />
                    </TableCell>
                    {tableHead.map((head) => (
                      <TableCell
                        key={head.id}
                        component="th"
                        scope="row"
                        padding="normal"
                      >
                        <Skeleton
                          width="100%"
                          animation="wave"
                          variant="text"
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : taxes.length === 0 ? (
                <TableNoData isNotFound={true} />
              ) : (
                taxes.map((row: ITax) => (
                  <TaxesTableRow
                    key={row.id}
                    row={row}
                    selected={row.id ? selected.includes(row.id) : false}
                    onSelectRow={() => row.id && onSelectRow(row.id)}
                    fetchData={fetchData}
                    handleOpenEditModal={() =>
                      row.id && handleOpenEditModal(row.id)
                    }
                    handleToggleDeleteModal={() =>
                      row.id && handleToggleDeleteModal(row.id)
                    }
                    translate={translate}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Scrollbar>
      <MyTablePagination
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
        onPageChange={handlePageChange}
        page={controller.page}
        count={totalRecords}
        rowsPerPage={controller.rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
};
